import * as React from 'react';
import {
  MdAdd,
  MdApartment,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdSearch,
  MdPauseCircle,
  MdVisibility,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { getAllOrganizations, deleteOrganization } from '../../Services/OrganizationService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';


const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
};

export default function OrganizationList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_VIEW, decodedToken);
  const canCreateOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_CREATE, decodedToken);
  const canEditOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_EDIT, decodedToken);
  const canDeleteOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_DELETE, decodedToken);

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleCreateOrganization = React.useCallback(() => {
    navigate(APPRoutes.ORGANIZATIONS_FORM);
  }, [navigate]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const res = await deleteOrganization(deleteTarget.Id, token);
      if (res?.is_success) {
        setRows((currentRows) => currentRows.filter((row) => row.Id !== deleteTarget.Id));
        setDeleteTarget(null);
        toast.success(res?.message || 'Organization deleted');
      } else if (res?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        console.error('Failed to delete organization', res);
        setDeleteError(res?.message || 'Failed to delete organization');
      } else {
        console.error('Failed to delete organization', res);
        setDeleteError(res?.message || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      setDeleteError('Something went wrong while deleting the organization.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesStatus = status === 'All' || row.StatusName === status;
      const matchesSearch =
        !term ||
        row.OrganizationName?.toLowerCase().includes(term) ||
        row.ClientName?.toLowerCase().includes(term) ||
        row.CreatedBy?.toLowerCase().includes(term) ||
        row.cid?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const loadOrganizations = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getAllOrganizations(token);
      if (response?.is_success) {
        setStatusSummary(response?.data?.Stats || response?.data?.StatusSummary || null);
        const items = Array.isArray(response?.data?.Organizations) ? response.data.Organizations : [];
        // Map API fields to existing UI column keys (support snake_case)
        const mapped = items.map((o) => {
          const id = o.id ?? o.Id ?? null;
          const account_id = o.account_id ?? o.AccountId ?? null;
          const account_name = o.account_name ?? o.AccountName ?? '';
          const cid = o.cid ?? o.Cid ?? '';
          const status_id = o.status_id ?? o.StatusId ?? null;
          const status_name = o.status_name ?? o.StatusName ?? (status_id === 1 ? 'Active' : 'Inactive');
          const group_office_name = o.group_office_name ?? o.GroupOfficeName ?? o.OrganizationName ?? '';
          const dba = o.dba ?? o.Dba ?? '';
          const established_on = o.established_on ?? o.EstablishedOn ?? '';
          const tax_id = o.tax_id ?? o.TaxId ?? '';
          const group_npi = o.group_npi ?? o.GroupNpi ?? '';
          const phone = o.phone ?? o.Phone ?? '';
          const fax = o.fax ?? o.Fax ?? null;
          const erx = o.erx ?? o.Erx ?? false;
          const drug_injection = o.drug_injection ?? o.DrugInjection ?? false;
          const drug_injection_notes = o.drug_injection_notes ?? o.DrugInjectionNotes ?? null;
          const labs = o.labs ?? o.Labs ?? false;
          const emr_name = o.emr_name ?? o.EmrName ?? null;
          const pm = o.pm ?? o.Pm ?? null;
          const clearing_house = o.clearing_house ?? o.ClearingHouse ?? null;
          const pos_list = o.pos_list ?? o.PosList ?? [];
          const avg_monthly_volume = o.avg_monthly_volume ?? o.AvgMonthlyVolume ?? null;
          const avg_monthly_collections = o.avg_monthly_collections ?? o.AvgMonthlyCollections ?? null;
          const mailing_address = o.mailing_address ?? o.MailingAddress ?? null;
          const pay_to_address = o.pay_to_address ?? o.PayToAddress ?? null;
          const taxonomies = o.taxonomies ?? o.Taxonomies ?? [];
          const created_on = o.created_on ?? o.CreatedOn ?? o.createdOn ?? null;
          const created_by = o.created_by ?? o.CreatedBy ?? o.createdBy ?? '';
          const modified_on = o.modified_on ?? o.ModifiedOn ?? o.modifiedOn ?? null;
          const modified_by = o.modified_by ?? o.ModifiedBy ?? o.modifiedBy ?? '';
          const handled_by = o.handled_by ?? o.HandledBy ?? o.handledBy ?? '';

          return {
            ...o,
            id,
            Id: id,
            account_id,
            account_name,
            cid,
            status_id,
            status_name,
            group_office_name,
            dba,
            established_on,
            tax_id,
            group_npi,
            phone,
            fax,
            erx,
            drug_injection,
            drug_injection_notes,
            labs,
            emr_name,
            pm,
            clearing_house,
            pos_list,
            avg_monthly_volume,
            avg_monthly_collections,
            mailing_address,
            pay_to_address,
            taxonomies,
            created_on,
            created_by,
            modified_on,
            modified_by,
            handled_by,
            // UI friendly fields
            OrganizationName: group_office_name,
            ClientName: account_name,
            StatusName: status_name,
            CreatedBy: created_by,
            HandledBy: handled_by,
          };
        });
        setRows(mapped);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal server error. Please Contact Support Team.');
        setRows([]);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const stats = React.useMemo(() => {
    const counts = statusSummary || rows.reduce((acc, row) => {
      const statusName = row.StatusName || 'Unknown';
      acc[statusName] = (acc[statusName] || 0) + 1;
      acc.Total = (acc.Total || 0) + 1;
      return acc;
    }, {});

    return [
      {
        label: 'Total Organizations',
        value: counts.Total ?? rows.length,
        icon: <MdApartment size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active Organizations',
        value: counts.Active || 0,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive Organizations',
        value: counts.Inactive || 0,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-icon-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ];
  }, [rows, statusSummary]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'OrganizationName',
        label: 'Organizations',
        minWidth: 220,
        format: (value, row) => canViewOrganization ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() =>
              navigate(APPRoutes.ORGANIZATIONS_VIEW.replace(':id', row.Id), {
                state: { organization: row },
              })
            }
          >
            {value}
          </button>
        ) : (
          <span className="text-dark">{value}</span>
        ),
      },
      {
        id: 'cid',
        label: 'CID',
        minWidth: 120,
        format: (value) => (
          <div className="fw-medium text-dark">{value || '-'}</div>
        ),
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'ClientName',
        label: 'Client',
        minWidth: 130,
      });
    }

    cols.push(
      {
        id: 'handled_by_name',
        label: 'Cred Member',
        minWidth: 180,
        format: (value) => <div className="fw-medium text-dark">{value || '-'}</div>,
      },
      {
        id: 'StatusName',
        label: 'Status',
        minWidth: 120,
        align: 'center',
        format: (value) => (
          <span
            className="badge rounded-pill fw-bolder px-3 py-2 text-center"
            style={{
              minWidth: '96px',
              fontWeight: 1000,
              ...(STATUS_BADGE_STYLE[value] || {
                backgroundColor: 'var(--hc-status-inactive-bg)',
                color: 'var(--hc-status-inactive-text)',
              }),
            }}
          >
            {value}
          </span>
        ),
      },
      {
        id: 'CreatedBy',
        label: 'CreatedBy',
        minWidth: 160,
      },
    );

    if (canViewOrganization || canEditOrganization || canDeleteOrganization) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 120,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewOrganization ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() =>
                  navigate(APPRoutes.ORGANIZATIONS_VIEW.replace(':id', row.Id), {
                    state: { organization: row },
                  })
                }
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditOrganization ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => navigate(APPRoutes.ORGANIZATIONS_EDIT.replace(':id', row.Id))}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteOrganization ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [
    navigate,
    handleDeleteClick,
    canViewOrganization,
    canEditOrganization,
    canDeleteOrganization,
    isSuperAdmin,
  ]);

  return (
    <>
      <PageTitle title="Organizations - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Organizations Management</h4>
          <p className="text-secondary mb-0 small">
            Manage all organizations and view their client relationships.
          </p>
        </div>
        {canCreateOrganization ? (
          <button
            onClick={handleCreateOrganization}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Organization</span>
          </button>
        ) : null}
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div
                className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
              />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                <span className={`fw-bold ${stat.valueClass} fs-5`}>
                  {stat.value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search By Organization, Client, Created By, CID..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">

             <SearchableDropdown
              placeholder="Select status"
              options={STATUS_DROPDOWN_OPTIONS}
              value={status}
              onChange={setStatus}
              showSearch={true}
            /> 


            {/* <FormControl fullWidth size="small">
              <InputLabel id="organization-status-label">Status</InputLabel>
              <Select
                labelId="organization-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "30%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Organization"
        entityLabel="organization"
        itemName={deleteTarget?.OrganizationName || deleteTarget?.ClientName || ''}
        description="You are about to remove this organization from the platform. This action is permanent."
        confirmText="Delete Organization"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
